import debounce from 'lodash/debounce'
import React from 'react'
import { useFetcher } from 'react-router'
import { useAggregationStore } from '~/routes/artworks+/store/aggregation-store'
import type {
    AggregationResult,
    AggregationResults,
    EntityAggregationResults,
} from '~/services/graphql/generated'

interface UseFilterSuggestionsParams {
    filterName: string
    isCurrentlyActiveFilter: boolean
    singleFilterSuggestionsEndpoint?: string
    // debounce duration in milliseconds
    debounceTime?: number
}

// for use with the AutoSuggestFilters component
export function useFilterSuggestions({
    filterName,
    isCurrentlyActiveFilter,
    singleFilterSuggestionsEndpoint = '/api/frontend/artworks/fetch-filter-suggestions',
    debounceTime = 500,
}: UseFilterSuggestionsParams): {
    suggestions: AggregationResult[]
    fetchSuggestions: (searchString: string) => void
    isLoading: boolean
} {
    const fetcher = useFetcher<{
        aggregations: EntityAggregationResults
    }>()

    const aggregationStore = useAggregationStore()
    const { aggregationResults } = aggregationStore

    // Get filter suggestions that were already loaded using the useFetchMultipleAggregations()
    // hook. For most filters, there are never any preloaded suggestions. For the discovery page,
    // the only auto-suggest filter with preloaded suggestions is objectType.
    // See useFetchMultipleAggregations() for more details.
    function getPreloadedSuggestions() {
        const filterResults =
            aggregationResults?.[
                filterName as Exclude<keyof AggregationResults, '__typename'>
            ]
        return filterResults
    }

    let isLoading: boolean

    // When the filter is active, we always use the fetcher defined inside this hook.
    // Otherwise, use the preloaded suggestions, if there are any.
    let suggestions: AggregationResult[] | null
    if (isCurrentlyActiveFilter) {
        isLoading = fetcher.state === 'loading'
        suggestions =
            (fetcher.data?.aggregations?.results as AggregationResult[]) ?? null
    } else {
        isLoading = aggregationStore.isLoading
        suggestions = getPreloadedSuggestions()?.results as AggregationResult[]
        if (!suggestions) {
            isLoading = true
        }
    }

    const fetcherLoad = fetcher.load

    const fetchSuggestions = React.useCallback(
        (value: string) => {
            // Get existing filters from URL
            const searchParams = new URLSearchParams(window.location.search)
            const existingFilters = searchParams.get('filters')
            const parsedFilters = existingFilters
                ? JSON.parse(existingFilters)
                : {}

            // Update only our filter while preserving others
            fetcherLoad(
                `${singleFilterSuggestionsEndpoint}/${filterName}?filters=${encodeURIComponent(
                    JSON.stringify({
                        ...parsedFilters,
                        [filterName]: { keys: [], searchTerms: [value] },
                    })
                )}`
            )
        },
        [fetcherLoad, filterName, singleFilterSuggestionsEndpoint]
    )

    const debouncedFetchSuggestions = React.useMemo(
        () => debounce(fetchSuggestions, debounceTime),
        [fetchSuggestions, debounceTime]
    )

    return {
        isLoading,
        suggestions,
        fetchSuggestions: debouncedFetchSuggestions,
    }
}
